import { Anchor, Button, Group, SegmentedControl, Tabs, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { IconBook, IconBrain, IconChartBar, IconListCheck, IconRefresh, IconSettings } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  getZendeskStats,
  listZendeskDrafts,
  type ZendeskDraftSummary,
  type ZendeskStats,
} from '@mygpt/app/apis/services/zendesk';
import { useBot, useBotStore } from '@mygpt/app/bot';
import ContainerHeader from '@/components/Headers/ContainerHeader';
import { useGPTStore } from '@/store';
import { Dashboard } from './Dashboard';
import { DetailDrawer } from './DetailDrawer';
import { FILTER_PARAMS, PAGE_SIZE, Queue, type QueueFilter } from './Queue';
import { ReplyRulesPanel } from './ReplyRulesPanel';

const DAYS_OPTIONS = [7, 30, 90] as const;

/**
 * 問い合わせ連携（Zendesk 回复环運営画面）：効果ダッシュボード / 回答改善キュー / 回答差分ドロワー。
 * 連携設定在 設定 > 連携アプリ；ナレッジ審査在沉淀先知识库的「ナレッジループ」tab（?tab=queue&unit= で直リンク）。
 */
export const ZendeskOps = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { botId } = useBot();
  const linkedDataset = useBotStore((s) => s.linkedDataset);
  const { userInfo } = useGPTStore();

  const [tab, setTab] = useState<string | null>('dashboard');
  const [days, setDays] = useState<number>(30);
  const [stats, setStats] = useState<ZendeskStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [filter, setFilter] = useState<QueueFilter>('all');
  const [grouped, setGrouped] = useState(false);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ZendeskDraftSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [selected, setSelected] = useState<ZendeskDraftSummary | null>(null);
  const [recent, setRecent] = useState<ZendeskDraftSummary[]>([]);

  // 沉淀先知识库（ナレッジ審査入口）：取 bot 绑定的第一个知识库
  const firstDatasetId = useMemo(() => {
    const ds: any = (linkedDataset || [])[0];
    return ds?.id || ds?.dataset_id || null;
  }, [linkedDataset]);

  const loadStats = useCallback(async () => {
    if (!botId) return;
    setStatsLoading(true);
    try {
      const [{ data }, recentRes] = await Promise.all([
        getZendeskStats(botId, days),
        // 効果 tab 下方「直近の要確認」：编辑后发送 / 自作 各最新 6 件
        listZendeskDrafts(botId, { status: 'answered_edited,answered_self', days, limit: 6, offset: 0 }),
      ]);
      setStats(data);
      setRecent(recentRes.data.items || []);
    } catch (e) {
      console.error('load zendesk stats failed:', e);
      showNotification({ message: t('zendesk_ops.load_error'), color: 'red' });
    } finally {
      setStatsLoading(false);
    }
  }, [botId, days, t]);

  const loadList = useCallback(async () => {
    if (!botId) return;
    setListLoading(true);
    try {
      const { data } = await listZendeskDrafts(botId, {
        ...FILTER_PARAMS[filter],
        group: grouped ? 'ticket' : undefined,
        days,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('load zendesk drafts failed:', e);
      showNotification({ message: t('zendesk_ops.load_error'), color: 'red' });
    } finally {
      setListLoading(false);
    }
  }, [botId, filter, grouped, days, page, t]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const counts = useMemo<Partial<Record<QueueFilter, number>>>(() => {
    if (!stats) return {};
    const s = stats.by_status || {};
    const sk = stats.by_skip_reason || {};
    return {
      all: stats.total,
      edited: s.answered_edited || 0,
      self: s.answered_self || 0,
      knowledge_gap: (sk.knowledge_gap || 0) + (sk.unanswerable || 0),
      posted: (s.posted || 0) + (s.inserted || 0) + (s.generating || 0),
      as_is: s.answered_as_is || 0,
      skipped: (s.skipped || 0) + (s.failed || 0) + (s.discarded || 0),
      dry_run: s.dry_run || 0,
    };
  }, [stats]);

  // タブのバッジ = 未対応の改善タスク（編集後送信・自作の未対応 + 未対応のナレッジ不足）だけ
  const pendingBadge = stats?.improvement
    ? (stats.improvement.pending || 0) + (stats.improvement.knowledge_gap_open || 0)
    : stats?.review?.pending || 0;

  const refresh = () => {
    loadStats();
    loadList();
  };

  const onChanged = (patch: Partial<ZendeskDraftSummary>) => {
    if (!selected) return;
    setItems((list) => list.map((d) => (d.id === selected.id ? { ...d, ...patch } : d)));
    setRecent((list) => list.map((d) => (d.id === selected.id ? { ...d, ...patch } : d)));
    setSelected((s) => (s ? { ...s, ...patch } : s));
  };

  const goQueue = (f: QueueFilter) => {
    setFilter(f);
    setPage(1);
    setTab('queue');
  };

  return (
    <div>
      <ContainerHeader
        title={t('zendesk_ops.title')}
        description={t('zendesk_ops.description')}
        rightSection={
          <Group gap={8}>
            <SegmentedControl
              size="xs"
              value={String(days)}
              onChange={(v) => {
                setDays(Number(v));
                setPage(1);
              }}
              data={DAYS_OPTIONS.map((d) => ({ value: String(d), label: t(`zendesk_ops.days_${d}`) }))}
            />
            <Button
              variant="default"
              size="xs"
              leftSection={<IconRefresh size={14} />}
              onClick={refresh}
              loading={statsLoading || listLoading}
            >
              {t('zendesk_ops.refresh')}
            </Button>
            <Button
              variant="default"
              size="xs"
              leftSection={<IconSettings size={14} />}
              onClick={() => navigate(`/bot/${botId}/settings#publish`)}
            >
              {t('zendesk_ops.link_settings')}
            </Button>
            {firstDatasetId && (
              <Button
                variant="default"
                size="xs"
                leftSection={<IconBook size={14} />}
                component={Link}
                to={`/bot/${botId}/datasets/${firstDatasetId}/data-source/zendesk?tab=queue`}
              >
                {t('zendesk_ops.link_review')}
              </Button>
            )}
          </Group>
        }
      />

      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb={16}>
          <Tabs.Tab value="dashboard" leftSection={<IconChartBar size={15} />}>
            {t('zendesk_ops.tab_dashboard')}
          </Tabs.Tab>
          <Tabs.Tab value="queue" leftSection={<IconListCheck size={15} />}>
            {t('zendesk_ops.tab_queue')}
            {pendingBadge > 0 ? (
              <Text component="span" fz={11} fw={700} c="orange.7" ml={6}>
                {pendingBadge}
              </Text>
            ) : null}
          </Tabs.Tab>
          <Tabs.Tab value="rules" leftSection={<IconBrain size={15} />}>
            {t('zendesk_ops.tab_rules')}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="dashboard">
          <Dashboard stats={stats} loading={statsLoading} recent={recent} onOpen={setSelected} onGoQueue={goQueue} />
          {stats && stats.total === 0 && (
            <Text fz={12.5} c="gray.6" mt={12}>
              {t('zendesk_ops.no_data_hint')}{' '}
              <Anchor fz={12.5} onClick={() => navigate(`/bot/${botId}/settings#publish`)}>
                {t('zendesk_ops.link_settings')}
              </Anchor>
            </Text>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="queue">
          <Queue
            items={items}
            total={total}
            page={page}
            loading={listLoading}
            filter={filter}
            counts={counts}
            grouped={grouped}
            onFilter={(f) => {
              setFilter(f);
              setPage(1);
            }}
            onGrouped={(v) => {
              setGrouped(v);
              setPage(1);
            }}
            onPage={setPage}
            onOpen={setSelected}
          />
        </Tabs.Panel>
        <Tabs.Panel value="rules">{botId && <ReplyRulesPanel robotId={botId} />}</Tabs.Panel>
      </Tabs>

      <DetailDrawer
        robotId={botId}
        datasetId={firstDatasetId}
        draft={selected}
        reviewerName={userInfo?.name || userInfo?.email}
        onClose={() => setSelected(null)}
        onChanged={onChanged}
      />
    </div>
  );
};

export default ZendeskOps;
